import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { TooltipButton } from "@/components/ui/TooltipButton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Send, User, Music, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { getDefaultCoverImage } from "@/utils/defaultCoverImage";
import { Textarea } from "@/components/ui/textarea";
import { hapticMedium, hapticSuccess, hapticError } from "@/utils/haptic";
import { LeaderboardBadge } from "@/components/LeaderboardBadge";
import { useLeaderboardPosition } from "@/hooks/useLeaderboardPosition";
import { GENRE_HIERARCHY, getParentGenre } from "@/constants/genres";


// Helper component to show leaderboard badge for an artist
function ArtistLeaderboardBadge({ artistId }) {
  const { data: position } = useLeaderboardPosition(artistId);
  return position ? <LeaderboardBadge position={position} /> : null;
}

export default function Submit() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [selectedTrack, setSelectedTrack] = useState(null);
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [matchedPlaylists, setMatchedPlaylists] = useState([]);
  const [selectedPlaylists, setSelectedPlaylists] = useState(new Set()); // Track which playlists user selected

  const [isLoading, setIsLoading] = useState(true);

  const loadUser = async (showLoading = true) => {
    if (showLoading) setIsLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setCurrentUser(null);
        return;
      }
      const { data: profile } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", session.user.id)
        .maybeSingle();
      setCurrentUser(profile);
    } catch (error) {
      console.error("Error loading user:", error);
      setCurrentUser(null);
    } finally {
      if (showLoading) setIsLoading(false);
    }
  };

  useEffect(() => {
    document.title = "Submit to Curators - Soundope";
    loadUser();
    
    // Re-check auth when page becomes visible (user returns from login)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadUser();
      }
    };
    
    // Listen for storage events (token changes from login in same tab)
    const handleStorageChange = (e) => {
      if (e.key === 'auth_token') {
        loadUser();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('storage', handleStorageChange);
    
    // Removed polling interval - it was causing infinite 401 errors and glitching
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []); // Empty deps - only run once on mount

  // Fetch user's tracks
  const { data: userTracks = [] } = useQuery({
    queryKey: ["user-tracks", currentUser?.id],
    enabled: !!currentUser,
    retry: false, // Prevent infinite retries
    staleTime: 30000,
    queryFn: async () => {
      const { data: tracks } = await supabase
        .from("tracks")
        .select("*")
        .eq("artist_id", currentUser.id)
        .eq("is_active", true);
      return tracks || [];
    },
  });

  // Sample playlists to ensure every genre has options
  const SAMPLE_PLAYLISTS = [
    // Rap / Hip-Hop
    { id: 'sample-1', name: 'Underground Horror Rap', genre: 'Horrorcore', follower_count: 12500, submission_price: 1, curator_id: 'system', is_sample: true },
    { id: 'sample-2', name: 'Trap Metal Mayhem', genre: 'Trap Metal', follower_count: 8700, submission_price: 1, curator_id: 'system', is_sample: true },
    { id: 'sample-3', name: 'Phonk Drift Club', genre: 'Phonk', follower_count: 45000, submission_price: 2, curator_id: 'system', is_sample: true },
    { id: 'sample-4', name: 'Sad Boy Hours', genre: 'Emo Rap', follower_count: 23000, submission_price: 1, curator_id: 'system', is_sample: true },
    { id: 'sample-5', name: 'Dark Trap Dungeon', genre: 'Dark Trap', follower_count: 15600, submission_price: 1, curator_id: 'system', is_sample: true },
    { id: 'sample-6', name: 'Cloud Rap Paradise', genre: 'Cloud Rap', follower_count: 19200, submission_price: 1, curator_id: 'system', is_sample: true },
    { id: 'sample-7', name: 'Boom Bap Revival', genre: 'Boom Bap', follower_count: 31000, submission_price: 2, curator_id: 'system', is_sample: true },
    { id: 'sample-8', name: 'UK Drill Central', genre: 'UK Drill', follower_count: 28400, submission_price: 2, curator_id: 'system', is_sample: true },
    { id: 'sample-9', name: 'US Drill Anthems', genre: 'US Drill', follower_count: 35000, submission_price: 2, curator_id: 'system', is_sample: true },
    { id: 'sample-10', name: 'Grime Lords', genre: 'Grime', follower_count: 18900, submission_price: 1, curator_id: 'system', is_sample: true },
    { id: 'sample-11', name: 'Street Stories', genre: 'Gangsta Rap', follower_count: 42000, submission_price: 2, curator_id: 'system', is_sample: true },
    { id: 'sample-12', name: 'Woke Bars Only', genre: 'Conscious Hip-Hop', follower_count: 14500, submission_price: 1, curator_id: 'system', is_sample: true },
    { id: 'sample-13', name: 'Mumble Vibes', genre: 'Mumble', follower_count: 56000, submission_price: 2, curator_id: 'system', is_sample: true },
    // R&B
    { id: 'sample-14', name: 'Alternative R&B Nights', genre: 'Alternative R&B', follower_count: 22000, submission_price: 1, curator_id: 'system', is_sample: true },
    { id: 'sample-15', name: 'Dark R&B Moods', genre: 'Dark R&B', follower_count: 17800, submission_price: 1, curator_id: 'system', is_sample: true },
    { id: 'sample-16', name: 'Trap Soul Sessions', genre: 'Trap Soul', follower_count: 29500, submission_price: 2, curator_id: 'system', is_sample: true },
    { id: 'sample-17', name: 'Neo-Soul Sanctuary', genre: 'Neo-Soul', follower_count: 21300, submission_price: 1, curator_id: 'system', is_sample: true },
    // Pop
    { id: 'sample-18', name: 'Hyperpop Explosion', genre: 'Hyperpop / Glitchcore', follower_count: 33000, submission_price: 2, curator_id: 'system', is_sample: true },
    { id: 'sample-19', name: 'Dark Pop Chronicles', genre: 'Dark Pop', follower_count: 19700, submission_price: 1, curator_id: 'system', is_sample: true },
    { id: 'sample-20', name: 'Lo-fi Pop Chill', genre: 'Lo-fi Pop', follower_count: 41000, submission_price: 2, curator_id: 'system', is_sample: true },
    { id: 'sample-21', name: 'Indie Pop Discoveries', genre: 'Indie Pop', follower_count: 52000, submission_price: 2, curator_id: 'system', is_sample: true },
    { id: 'sample-22', name: 'Synth Pop Dreams', genre: 'Synth Pop', follower_count: 27600, submission_price: 1, curator_id: 'system', is_sample: true },
    { id: 'sample-23', name: 'Dream Pop Haze', genre: 'Dream Pop', follower_count: 18400, submission_price: 1, curator_id: 'system', is_sample: true },
    { id: 'sample-24', name: 'Art Pop Gallery', genre: 'Art Pop', follower_count: 14200, submission_price: 1, curator_id: 'system', is_sample: true },
    // Rock
    { id: 'sample-25', name: 'Grunge Revival', genre: 'Grunge', follower_count: 38000, submission_price: 2, curator_id: 'system', is_sample: true },
    { id: 'sample-26', name: 'Industrial Noise', genre: 'Industrial Rock', follower_count: 12800, submission_price: 1, curator_id: 'system', is_sample: true },
    { id: 'sample-27', name: 'Post-Punk Underground', genre: 'Post-Punk', follower_count: 16500, submission_price: 1, curator_id: 'system', is_sample: true },
    { id: 'sample-28', name: 'Pop Punk Forever', genre: 'Pop Punk', follower_count: 47000, submission_price: 2, curator_id: 'system', is_sample: true },
    // Goth / Darkwave
    { id: 'sample-29', name: 'Goth Rock Crypt', genre: 'Goth Rock', follower_count: 15300, submission_price: 1, curator_id: 'system', is_sample: true },
    { id: 'sample-30', name: 'Darkwave Abyss', genre: 'Darkwave', follower_count: 11200, submission_price: 1, curator_id: 'system', is_sample: true },
    { id: 'sample-31', name: 'Ethereal Voices', genre: 'Ethereal Wave', follower_count: 9800, submission_price: 1, curator_id: 'system', is_sample: true },
    { id: 'sample-32', name: 'Deathrock Dungeon', genre: 'Deathrock', follower_count: 7500, submission_price: 1, curator_id: 'system', is_sample: true },
    { id: 'sample-33', name: 'Coldwave Freeze', genre: 'Coldwave', follower_count: 8900, submission_price: 1, curator_id: 'system', is_sample: true },
    // Metal
    { id: 'sample-34', name: 'Black Metal Void', genre: 'Black Metal / DSBM', follower_count: 21000, submission_price: 1, curator_id: 'system', is_sample: true },
    { id: 'sample-35', name: 'Death Metal Pit', genre: 'Death Metal / Deathcore', follower_count: 34500, submission_price: 2, curator_id: 'system', is_sample: true },
    { id: 'sample-36', name: 'Thrash Attack', genre: 'Thrash Metal', follower_count: 28700, submission_price: 2, curator_id: 'system', is_sample: true },
    { id: 'sample-37', name: 'Doom & Stoner Riffs', genre: 'Heavy / Doom / Stoner', follower_count: 19400, submission_price: 1, curator_id: 'system', is_sample: true },
    { id: 'sample-38', name: 'Metalcore Breakdown', genre: 'Metalcore', follower_count: 43000, submission_price: 2, curator_id: 'system', is_sample: true },
    { id: 'sample-39', name: 'Nu-Metal Nostalgia', genre: 'Nu-Metal / Rap Metal', follower_count: 51000, submission_price: 2, curator_id: 'system', is_sample: true },
    { id: 'sample-40', name: 'Grindcore Blast', genre: 'Grindcore', follower_count: 8200, submission_price: 1, curator_id: 'system', is_sample: true },
    { id: 'sample-41', name: 'Prog Metal Masters', genre: 'Progressive / Technical Metal', follower_count: 24600, submission_price: 1, curator_id: 'system', is_sample: true },
    // Electronic
    { id: 'sample-42', name: 'Witch House Ritual', genre: 'Witch House', follower_count: 16700, submission_price: 1, curator_id: 'system', is_sample: true },
    { id: 'sample-43', name: 'Dark Ambient Depths', genre: 'Dark Ambient / Drone', follower_count: 11500, submission_price: 1, curator_id: 'system', is_sample: true },
    { id: 'sample-44', name: 'Breakcore Chaos', genre: 'Breakcore', follower_count: 13900, submission_price: 1, curator_id: 'system', is_sample: true },
    // Punk / Hardcore
    { id: 'sample-45', name: 'Hardcore Fury', genre: 'Hardcore Punk', follower_count: 22500, submission_price: 1, curator_id: 'system', is_sample: true },
    { id: 'sample-46', name: 'Folk Punk Rebellion', genre: 'Folk Punk', follower_count: 14100, submission_price: 1, curator_id: 'system', is_sample: true },
    { id: 'sample-47', name: 'Ska Party', genre: 'Ska', follower_count: 31200, submission_price: 2, curator_id: 'system', is_sample: true },
    { id: 'sample-48', name: 'Two-Tone Classics', genre: 'Two-Tone', follower_count: 9600, submission_price: 1, curator_id: 'system', is_sample: true },
    { id: 'sample-49', name: 'Ska Punk Power', genre: 'Ska Punk', follower_count: 26800, submission_price: 1, curator_id: 'system', is_sample: true },
    { id: 'sample-50', name: 'Reggae Ska Vibes', genre: 'Reggae Ska', follower_count: 18300, submission_price: 1, curator_id: 'system', is_sample: true },
  ];

  // Fetch active playlists from curator_playlists table
  const { data: activePlaylists = [] } = useQuery({
    queryKey: ["active-playlists"],
    retry: false,
    staleTime: 60000,
    queryFn: async () => {
      console.log("[Submit] Fetching curator playlists...");
      
      // Use Supabase directly
      let apiPlaylists = [];
      
      try {
        const { data: playlists, error } = await supabase
          .from("curator_playlists")
          .select("*")
          .eq("is_active", true);
        
        console.log("[Submit] Curator playlists from Supabase:", playlists);
        
        if (!error && playlists) {
          // Filter out playlists with invalid names (Spotify IDs, gibberish, etc.)
          apiPlaylists = playlists.filter(p => {
            const name = p.playlist_name || p.name || '';
            // Invalid if: empty, looks like a Spotify ID (22+ alphanumeric), or contains "?"
            if (!name || name.length < 3) return false;
            if (/^[a-zA-Z0-9]{20,}$/.test(name)) return false; // Spotify-like ID
            if (name.includes('?')) return false; // URL params
            return true;
          });
        }
      } catch (error) {
        console.error("[Submit] Error fetching playlists:", error);
      }
      
      // Combine API playlists with sample playlists (samples fill genre gaps)
      const allPlaylists = [...apiPlaylists];
      
      // Get genres covered by API playlists
      const coveredGenres = new Set(apiPlaylists.map(p => (p.genre || '').toLowerCase()));
      
      // Add sample playlists for genres not covered
      SAMPLE_PLAYLISTS.forEach(sample => {
        const sampleGenreLower = sample.genre.toLowerCase();
        if (!coveredGenres.has(sampleGenreLower)) {
          allPlaylists.push(sample);
          coveredGenres.add(sampleGenreLower);
        }
      });
      
      console.log("[Submit] Combined playlists (API + samples):", allPlaylists.length);
      return allPlaylists;
    },
  });

  // When a track is selected, find matching playlists by genre
  const selectedTrackGenres = selectedTrack ? (userTracks.find(t => t.id === selectedTrack)?.genres?.join(',') || '') : '';
  const playlistsCount = activePlaylists.length;

  useEffect(() => {
    if (!selectedTrack || !playlistsCount) {
      setMatchedPlaylists([]);
      return;
    }

    const trackData = userTracks.find(t => t.id === selectedTrack);
    if (!trackData) {
      setMatchedPlaylists([]);
      return;
    }

    const trackGenres = trackData.genres || [];
    
    // Build a set of all genre terms to match (subgenres + their parent genres)
    const genreTerms = new Set();
    trackGenres.forEach(genre => {
      const lowerGenre = genre.toLowerCase();
      genreTerms.add(lowerGenre);
      
      // Also add the parent genre for matching
      const parent = getParentGenre(genre);
      if (parent) {
        genreTerms.add(parent.toLowerCase());
        // Add simplified versions (e.g., "Rap / Hip-Hop" -> "rap", "hip-hop", "hiphop")
        parent.toLowerCase().split(/[\s\/\-]+/).forEach(word => {
          if (word.length > 2) genreTerms.add(word);
        });
      }
      // Add simplified versions of the subgenre too
      lowerGenre.split(/[\s\/\-]+/).forEach(word => {
        if (word.length > 2) genreTerms.add(word);
      });
    });
    
    // Match playlists by genre field
    const matched = activePlaylists.filter(playlist => {
      if (!playlist.genre) return false;
      
      const playlistGenre = playlist.genre.toLowerCase();
      
      // Check if any of our genre terms match the playlist genre
      for (const term of genreTerms) {
        if (playlistGenre.includes(term) || term.includes(playlistGenre)) {
          return true;
        }
      }
      
      // Also check simplified versions
      const playlistTerms = playlistGenre.split(/[\s\/\-]+/).filter(w => w.length > 2);
      for (const pTerm of playlistTerms) {
        if (genreTerms.has(pTerm)) {
          return true;
        }
      }
      
      return false;
    });

    setMatchedPlaylists(matched);
    // Auto-select all matched playlists initially
    setSelectedPlaylists(new Set(matched.map(p => p.id)));
  }, [selectedTrack, playlistsCount, selectedTrackGenres, userTracks, activePlaylists]);

  // Toggle playlist selection
  const togglePlaylistSelection = (playlistId) => {
    setSelectedPlaylists(prev => {
      const newSet = new Set(prev);
      if (newSet.has(playlistId)) {
        newSet.delete(playlistId);
      } else {
        newSet.add(playlistId);
      }
      return newSet;
    });
  };

  // Calculate total cost in credits (submission_price is already in credits, default 1)
  const selectedPlaylistsData = matchedPlaylists.filter(p => selectedPlaylists.has(p.id));
  const totalCost = selectedPlaylistsData.reduce((sum, p) => sum + (p.submission_price || 1), 0);
  const userCredits = (currentUser?.premium_credits || 0) / 100; // Convert cents to dollars
  const canAfford = userCredits >= totalCost;

  const handleSubmit = async () => {
    if (!selectedTrack || !currentUser) {
      hapticError();
      toast.error("Please select a track");
      return;
    }

    if (selectedPlaylists.size === 0) {
      hapticError();
      toast.error("Please select at least one playlist");
      return;
    }

    // Check if user can afford
    if (!canAfford) {
      hapticError();
      toast.error(`Not enough credits. You need ${totalCost} credits but have ${userCredits}`);
      return;
    }

    hapticMedium();
    setIsSubmitting(true);

    try {
      const track = userTracks.find(t => t.id === selectedTrack);
      const costInCents = totalCost * 100; // Convert credits to cents

      // Deduct credits directly in Supabase
      const newBalance = (currentUser.premium_credits || 0) - costInCents;
      const { error: deductError } = await supabase
        .from("profiles")
        .update({ premium_credits: newBalance })
        .eq("id", currentUser.id);

      if (deductError) {
        throw new Error(`Failed to deduct credits: ${deductError.message}`);
      }

      // Update local state
      setCurrentUser(prev => ({
        ...prev,
        premium_credits: newBalance
      }));
      
      // Dispatch event so Layout navbar updates too
      window.dispatchEvent(new CustomEvent('credits:updated', { 
        detail: { newBalance } 
      }));

      // Log the transaction
      await supabase.from("credit_transactions").insert({
        user_id: currentUser.id,
        transaction_type: "playlist_pitch",
        amount: totalCost,
        credits_added: -costInCents,
        balance_before: currentUser.premium_credits || 0,
        balance_after: newBalance,
        metadata: { playlists: selectedPlaylistsData.length, track_id: selectedTrack }
      });

      // Create submissions for selected playlists only
      for (const playlist of selectedPlaylistsData) {
        await supabase.from("playlist_submissions").insert({
          user_id: currentUser.id,
          track_id: selectedTrack,
          playlist_name: playlist.name || playlist.playlist_name,
          status: "pending"
        });

        // Send notification to the playlist curator (only if not a sample playlist)
        if (playlist.curator_id && playlist.curator_id !== 'system') {
          try {
            await supabase.from("notifications").insert({
              user_id: playlist.curator_id,
              title: "🎵 New Track Pitch",
              message: `${currentUser.username} pitched "${track?.title}" to your playlist "${playlist.name || playlist.playlist_name}"${message ? `: "${message}"` : ''}`,
              is_read: false
            });
          } catch (notifError) {
            console.warn("Could not send notification:", notifError);
          }
        }
      }

      hapticSuccess();
      
      // Navigate to success page with submission details
      navigate("/submit-success", {
        state: {
          trackTitle: track?.title,
          playlistCount: selectedPlaylistsData.length,
          totalCost: totalCost
        }
      });
    } catch (error) {
      console.error("Submission error:", error);
      hapticError();
      toast.error(error.message || "Failed to submit track");
    } finally {
      setIsSubmitting(false);
    }
  };

  const isUnauthenticated = !currentUser && !isLoading;

  // Show loading state while checking auth
  if (isLoading) {
    return (
      <div className="min-h-screen px-4 py-8 pb-32 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#a0a0a0]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen px-4 py-8 pb-32">
      <div className="max-w-2xl mx-auto">
        <div className="neuro-base rounded-3xl p-8 mb-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="neuro-pressed w-16 h-16 rounded-2xl flex items-center justify-center">
              <Send className="w-8 h-8 text-[#a0a0a0]" />
            </div>
            <div>
              <h1 className="text-3xl font-light text-[#d0d0d0]">Pitch to Playlists</h1>
              <p className="text-sm text-[#808080]">Match your track with curators by genre</p>
            </div>
          </div>

          {isUnauthenticated && (
            <div className="mb-8 p-6 md:p-8 rounded-2xl bg-[#1a1a1a] border-2 border-[#3a3a3a] text-center">
              <h3 className="text-xl md:text-2xl font-medium text-[#d0d0d0] mb-3">
                Sign In Required
              </h3>
              <p className="text-sm md:text-base text-[#a0a0a0] mb-6">
                You need to be signed in to submit tracks to curators.
              </p>
              <Button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log("Sign in button clicked, navigating to /auth");
                  window.location.href = "/auth";
                }}
                size="lg"
                className="px-8 py-3 text-base md:text-lg rounded-xl bg-primary hover:bg-primary/90 transition-all hover:scale-105"
              >
                Sign In to Continue
              </Button>
            </div>
          )}


          {/* Step 1: Select Track */}
          <div className="mb-6">
            <label className="block text-sm text-[#a0a0a0] mb-3 flex items-center gap-2">
              <span className="neuro-pressed w-6 h-6 rounded-full flex items-center justify-center text-xs">1</span>
              Select Your Track
            </label>
            {isUnauthenticated ? (
              <div className="neuro-pressed rounded-2xl p-6 text-center">
                <Music className="w-12 h-12 mx-auto text-[#606060] mb-3" />
                <p className="text-[#808080] mb-3">Please sign in to view your tracks</p>
              </div>
            ) : userTracks.length === 0 ? (
              <div className="neuro-pressed rounded-2xl p-6 text-center">
                <Music className="w-12 h-12 mx-auto text-[#606060] mb-3" />
                <p className="text-[#808080] mb-3">You haven't uploaded any tracks yet</p>
                <TooltipButton 
                  onClick={() => navigate("/upload")} 
                  className="neuro-base active:neuro-pressed"
                  tooltip="Go to upload page to add your first track"
                >
                  Upload Track
                </TooltipButton>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-3">
                {userTracks.map((track) => (
                  <TooltipProvider key={track.id}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          onClick={() => setSelectedTrack(selectedTrack === track.id ? null : track.id)}
                          className={`neuro-flat rounded-2xl p-4 text-left smooth-transition ${
                            selectedTrack === track.id ? "ring-2 ring-purple-500" : ""
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <img
                              src={track.cover_image_url || getDefaultCoverImage(track.id)}
                              alt={track.title}
                              className="w-16 h-16 rounded-xl object-cover bg-[#1a1a1a]"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = getDefaultCoverImage(track.id);
                              }}
                            />
                            <div className="flex-1 min-w-0">
                              <h3 className="text-sm font-medium text-[#d0d0d0] truncate">{track.title}</h3>
                              <p className="text-xs text-[#808080] flex items-center gap-1.5">
                                {track.artist_name}
                                {track.artist_id && <ArtistLeaderboardBadge artistId={track.artist_id} />}
                              </p>
                              {track.genres?.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {track.genres.slice(0, 2).map((genre, i) => (
                                    <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-[#2a2a2a] text-[#909090]">
                                      {genre}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                            {selectedTrack === track.id && (
                              <Check className="w-5 h-5 text-purple-500" />
                            )}
                          </div>
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{selectedTrack === track.id ? "Click to deselect" : "Select this track"}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ))}
              </div>
            )}
          </div>

          {/* Step 2: Matched Playlists Preview */}
          {selectedTrack && (
            <div className="mb-6">
              <label className="block text-sm text-[#a0a0a0] mb-3 flex items-center gap-2">
                <span className="neuro-pressed w-6 h-6 rounded-full flex items-center justify-center text-xs">2</span>
                Matched Playlists ({matchedPlaylists.length})
              </label>
              {matchedPlaylists.length === 0 ? (
                <div className="neuro-pressed rounded-2xl p-8 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#1a1a1a] flex items-center justify-center">
                    <Music className="w-8 h-8 text-[#505050]" />
                  </div>
                  <h3 className="text-lg font-medium text-[#d0d0d0] mb-2">No Matching Playlists</h3>
                  <p className="text-sm text-[#808080] mb-4">
                    No playlists currently match your track's genre.
                  </p>
                  <p className="text-xs text-[#606060]">
                    Try selecting a different track or check back later as new playlists are added.
                  </p>
                </div>
              ) : (
                <div className="neuro-pressed rounded-2xl p-4">
                  <p className="text-xs text-[#808080] mb-3">Click to select/deselect playlists:</p>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {matchedPlaylists.map((playlist) => {
                      const isSelected = selectedPlaylists.has(playlist.id);
                      const rate = playlist.submission_price || 1; // submission_price is in credits
                      return (
                        <button
                          key={playlist.id}
                          onClick={() => togglePlaylistSelection(playlist.id)}
                          className={`w-full flex items-center gap-3 rounded-xl px-3 py-3 transition-all ${
                            isSelected 
                              ? 'bg-purple-500/20 ring-1 ring-purple-500' 
                              : 'bg-[#1a1a1a] hover:bg-[#252525]'
                          }`}
                        >
                          <div className={`w-5 h-5 rounded-md flex items-center justify-center ${
                            isSelected ? 'bg-purple-500' : 'bg-[#2a2a2a]'
                          }`}>
                            {isSelected && <Check className="w-3 h-3 text-white" />}
                          </div>
                          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500/20 to-blue-500/20 flex items-center justify-center">
                            <Music className="w-4 h-4 text-purple-400" />
                          </div>
                          <div className="flex-1 text-left">
                            <span className="text-sm text-[#d0d0d0] block">{playlist.name || playlist.playlist_name}</span>
                            <span className="text-xs text-[#808080]">{playlist.genre}</span>
                          </div>
                          <div className="text-right">
                            <span className="text-sm font-medium text-green-400">{rate} credit{rate !== 1 ? 's' : ''}</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  
                  {/* Total cost summary */}
                  <div className="mt-4 pt-3 border-t border-[#2a2a2a] flex items-center justify-between">
                    <div>
                      <span className="text-sm text-[#808080]">Selected: </span>
                      <span className="text-sm text-[#d0d0d0]">{selectedPlaylists.size} playlist{selectedPlaylists.size !== 1 ? 's' : ''}</span>
                    </div>
                    <div className="text-right">
                      <span className="text-sm text-[#808080]">Total: </span>
                      <span className={`text-lg font-bold ${canAfford ? 'text-green-400' : 'text-red-400'}`}>
                        {totalCost} credits
                      </span>
                      {!canAfford && (
                        <p className="text-xs text-red-400">You have {userCredits} credits</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}


          {/* Optional Message */}
          {selectedTrack && matchedPlaylists.length > 0 && (
            <div className="mb-6">
              <label className="block text-sm text-[#a0a0a0] mb-3">Message (Optional)</label>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Add a message to the playlist curators..."
                maxLength={200}
                className="neuro-pressed rounded-2xl bg-[#0a0a0a] border-none text-[#d0d0d0] placeholder:text-[#606060] min-h-[80px]"
              />
              <p className="text-xs text-[#606060] mt-1 text-right">{message.length}/200</p>
            </div>
          )}

          {/* Submit Button */}
          <TooltipButton
            onClick={handleSubmit}
            disabled={!selectedTrack || selectedPlaylists.size === 0 || isSubmitting || isUnauthenticated || !canAfford}
            className="w-full neuro-base active:neuro-pressed rounded-2xl py-6 text-lg disabled:opacity-50"
            tooltip={
              isUnauthenticated 
                ? "Please sign in to submit tracks" 
                : !selectedTrack
                  ? "Select a track first"
                  : selectedPlaylists.size === 0
                    ? "Select at least one playlist"
                    : !canAfford
                      ? `Not enough credits (need ${totalCost}, have ${userCredits})`
                      : `Pitch to ${selectedPlaylists.size} playlist${selectedPlaylists.size > 1 ? 's' : ''} for ${totalCost} credits`
            }
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Pitching...
              </>
            ) : (
              <>
                <Send className="w-5 h-5 mr-2" />
                Pitch to {selectedPlaylists.size} Playlist{selectedPlaylists.size !== 1 ? 's' : ''} ({totalCost} credits)
              </>
            )}
          </TooltipButton>
        </div>
      </div>
    </div>
  );
}
